import React from 'react';
import {
  IconWrapper,
  StyledButtonWrapper,
  TextWrapper,
  TrailingSlot,
  SelectedCheck,
} from './styled-button.styled';
import { Text, font } from '../../style-utils/text';
import { palette } from '../../style-utils/theme';
import { CountBadge } from '../count-badge/count-badge';

interface StyledButtonProps {
  text: string;
  icon?: React.ReactNode;
  isSelected: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  path?: string;
  disabled?: boolean;
  pending?: boolean;
  /**
   * A count in the trailing slot (CountBadge, brand). Not drawn at 0. The badge
   * is aria-hidden, so pass `ariaLabel` with the number in it too.
   */
  badge?: number;
  /** Overrides the accessible name — "Expected, 3 today" for a badged card. */
  ariaLabel?: string;
}

export const StyledButton: React.FC<StyledButtonProps> = ({
  text,
  icon,
  isSelected,
  onClick,
  path,
  disabled = false,
  pending = false,
  badge,
  ariaLabel,
}) => {
  const isMainMenu = path && path === '/main-menu';
  const hasBadge = typeof badge === 'number' && badge > 0;

  return (
    <StyledButtonWrapper
      type="button"
      onClick={onClick}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      aria-pressed={isSelected}
      aria-label={ariaLabel}
      isSelected={isSelected}
    >
      <IconWrapper>{isMainMenu ? icon : null}</IconWrapper>
      <TextWrapper>
        <Text
          as="span"
          align="center"
          fontWeight={600}
          fontSize={font.size.m}
          color={isSelected ? palette.ink : palette.slate}
        >
          {text}
        </Text>
      </TextWrapper>
      <TrailingSlot $hasBadge={hasBadge}>
        {hasBadge ? (
          <CountBadge count={badge as number} tone="brand" />
        ) : (
          !isMainMenu &&
          (isSelected || pending) && <SelectedCheck aria-hidden="true" />
        )}
      </TrailingSlot>
    </StyledButtonWrapper>
  );
};
